import React, { useEffect, useMemo, useRef, useState } from "react";
import "./CustomTable.css";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import FilterInput from "./filterInput/FilterInput";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import type { TranslationKeys } from "../../../i18n";
import MultiSelectFilterPopup from "../FilterPopup/MultiSelectFilterPopup";

export interface TableColumn {
  id: string;
  label: string;
  sticky?: boolean;
  format?: (value: any, row: any) => React.ReactNode;
  hideOnMobile?: boolean;
  minWidth?: number;
  maxWidth?: number;
  align?: "left" | "center" | "right";
  isSearchable?: boolean;
  isSortable?: boolean;
  hasCalendar?: boolean;
  filterable?: boolean;
  colType?: "text" | "number" | "date" | "boolean";
}

export interface PaginationConfig {
  enabled: boolean;
  page: number;
  pageSize: number;
  pageSizeOptions: number[];
  showPageSizeSelector: boolean;
  total: number;
  onPageChange?: (newPage: number) => void;
  onPageSizeChange?: (newPageSize: number) => void;
}

export interface CustomTableProps {
  columns: TableColumn[];
  data: any[];
  pagination?: PaginationConfig;
  loading?: boolean;
  onRowClick?: (row: any) => void;
  emptyMessage?: string;
  showRowNumbers?: boolean;
  maxHeight?: string;
  onSearch?: (searchValue: string) => void;
  onSort?: (sortField: string, sortOrder: "asc" | "desc") => void;
  onFilter?: (filters: Record<string, string>) => void;
  businessUnits?: { id: number; name: string }[];
  debounceTime?: number; // New prop for custom debounce time
}

const CustomTable: React.FC<CustomTableProps> = ({
  columns,
  data,
  pagination,
  loading = false,
  onRowClick,
  emptyMessage = "No data available",
  showRowNumbers = false,
  onFilter,
  onSearch,
  onSort,
  businessUnits = [],
  debounceTime = 300, // Default debounce time of 300ms
}) => {
  const { t } = useTranslation<TranslationKeys>();
  const [searchValues, setSearchValues] = useState<Record<string, string>>({});
  const [sortState, setSortState] = useState<{
    field: string | null;
    order: "asc" | "desc";
  }>({ field: null, order: "asc" });

  const [filterDropdown, setFilterDropdown] = useState<string | null>(null);
  const [selectedBU, setSelectedBU] = useState<(string | number)[]>([]);
  const visibleColumns = useMemo(() => columns, [columns]);
  const [calendarDropdown, setCalendarDropdown] = useState<string | null>(null);
  const [selectedDates, setSelectedDates] = useState<Record<string, Date | null>>({});
  const filterRef = useRef<HTMLDivElement | null>(null);
  const calendarRef = useRef<HTMLDivElement | null>(null);
  // Ref to store debounce timeouts
  const debounceRef = useRef<Record<string, NodeJS.Timeout>>({});

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarDropdown && calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setCalendarDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [calendarDropdown]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterDropdown && // if dropdown is open
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setFilterDropdown(null); // close dropdown
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [filterDropdown]);

  // Cleanup timeouts on component unmount
  useEffect(() => {
    return () => {
      Object.values(debounceRef.current).forEach(clearTimeout);
    };
  }, []);

  const handleSearchChange = (columnId: string, value: string) => {
    setSearchValues((prev) => ({ ...prev, [columnId]: value }));
    
    // Clear previous timeout for this column
    if (debounceRef.current[columnId]) {
      clearTimeout(debounceRef.current[columnId]);
    }
    
    // Set new timeout for debounced search
    debounceRef.current[columnId] = setTimeout(() => {
      onSearch?.(value);
    }, debounceTime);
  };

  const handleSort = (colId: string) => {
    let newOrder: "asc" | "desc" = "asc";
    if (sortState.field === colId && sortState.order === "asc") {
      newOrder = "desc";
    }
    setSortState({ field: colId, order: newOrder });
    onSort?.(colId, newOrder);
  };

  const handleFilterClick = (colId: string) => {
    setFilterDropdown((prev) => (prev === colId ? null : colId));
  };

  const handleCalendarClick = (colId: string) => {
    setCalendarDropdown(prev => (prev === colId ? null : colId));
  };

  const handleDateChange = (colId: string, date: Date | null) => {
    if (!date) {
      setSelectedDates(prev => ({ ...prev, [colId]: null }));
      handleSearchChange(colId, "");
    } else {
      setSelectedDates(prev => ({ ...prev, [colId]: date }));
  
      const formatted = [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0"),
      ].join("-");
  
      handleSearchChange(colId, formatted);
    }
  
    setCalendarDropdown(null);
  };
  
  const handleImmediateFilterChange = (filters: Record<string, string>) => {
    onFilter?.(filters);
  };

  return (
    <div className="custom-table-container">
      <div className="custom-table-content">
        <div className="custom-table-wrapper">
          <table className="custom-table">
            <thead>
              <tr>
                {showRowNumbers && <th>#</th>}
                {visibleColumns.map((col) => (
                  <th key={col.id}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        position: "relative",
                      }}
                      className="table-header-cell"
                    >
                      <span
                        style={{
                          cursor: col.isSortable ? "pointer" : "default",
                        }}
                        onClick={() => col.isSortable && handleSort(col.id)}
                      >
                        {col.label}
                      </span>

                      {(col.isSearchable ||
                        col.hasCalendar ||
                        col.filterable) && (
                        <FilterInput
                          value={searchValues[col.id] || ""}
                          onChange={(value) => handleSearchChange(col.id, value)}
                          onSort={
                            col.isSortable ? () => handleSort(col.id) : undefined
                          }
                          filterable={col.filterable}
                          hasCalendar={col.hasCalendar}
                          sortDirection={sortState.order}
                          onFilterClick={() => handleFilterClick(col.id)}
                          onCalendarClick={() => handleCalendarClick(col.id)}
                          isActiveFilter={
                            !!(
                              (searchValues[col.id] && searchValues[col.id] !== "") ||
                              (col.id === "bu_name" && selectedBU.length > 0) ||
                              (col.hasCalendar && selectedDates[col.id])
                            )
                          }
                        />
                      )}
                    </div>

                    {filterDropdown === col.id && col.id === "bu_name" && (
                      <div ref={filterRef}>
                        <MultiSelectFilterPopup
                          options={businessUnits.map((bu) => ({
                            id: bu.id,
                            name: bu.name,
                          }))}
                          selectedOptions={selectedBU}
                          onCancel={() => setFilterDropdown(null)}
                          onApply={(selectedIds) => {
                            setSelectedBU(selectedIds);
                            if (selectedIds.length > 0) {
                              handleImmediateFilterChange({ bu: selectedIds.join(",") });
                            } else {
                              handleImmediateFilterChange({});
                            }
                            setFilterDropdown(null);
                          }}
                        />
                      </div>
                    )}

                    {calendarDropdown === col.id && col.hasCalendar && (
                      <div
                        ref={calendarRef}
                        className="calendar-popup"
                        style={{ position: "absolute", zIndex: 1000, left: 15 }}
                      >
                        <DatePicker
                          selected={selectedDates[col.id] || null}
                          onChange={(date: Date | null) => handleDateChange(col.id, date)}
                          inline
                        />
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={visibleColumns.length + (showRowNumbers ? 1 : 0)}
                    style={{ textAlign: "center" }}
                  >
                    {t("loading")}
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr style={{height: "250px" }}>
                  <td
                    colSpan={visibleColumns.length + (showRowNumbers ? 1 : 0)}
                    style={{ textAlign: "center", zIndex: 0 }}
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                data?.map((row, rowIndex) => (
                  <tr
                    key={row.id || rowIndex}
                    onClick={() => onRowClick?.(row)}
                    style={{ cursor: onRowClick ? "pointer" : "default" }}
                  >
                    {showRowNumbers && <td>{rowIndex + 1}</td>}
                    {visibleColumns.map((col) => (
                      <td key={col.id}>
                        {col.format
                          ? col.format(row[col.id], row)
                          : row[col.id] ?? "-"}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination?.enabled && pagination.total > 0 && (
          <div
            className="custom-table-footer"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              borderTop: "1px solid #eee",
              width: "100%",
              padding: "6px 8px",
              fontSize: "12px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span>{t("rows_per_page")}</span>

              {pagination.showPageSizeSelector && (
                <select
                  value={pagination.pageSize}
                  onChange={(e) =>
                    pagination.onPageSizeChange?.(Number(e.target.value))
                  }
                  style={{
                    height: "25px",
                    padding: "2px 4px",
                    fontSize: "12px",
                    border: "1px solid #ccc",
                    background: "#fff",
                    cursor: "pointer",
                  }}
                >
                  {pagination.pageSizeOptions?.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              )}

              <span>
                {(pagination.page - 1) * pagination.pageSize + 1}–
                {Math.min(
                  pagination.page * pagination.pageSize,
                  pagination.total
                )}{" "}
                of {pagination.total}
              </span>

              <button
                onClick={() => pagination.onPageChange?.(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                <NavigateBeforeIcon fontSize="small" />
              </button>

              <button
                onClick={() => pagination.onPageChange?.(pagination.page + 1)}
                disabled={
                  pagination.page >=
                  Math.ceil(pagination.total / pagination.pageSize)
                }
              >
                <NavigateNextIcon fontSize="small" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomTable;
